import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Award, Users, Send, Calendar, Eye, CheckCircle, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminGoTalent = () => {
  const [candidates, setCandidates] = useState([]);
  const [passes, setPasses] = useState([]);
  const [offers, setOffers] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [candidatesRes, passesRes, offersRes, interviewsRes] = await Promise.all([
        axios.get(`${API}/admin/talent/candidates`),
        axios.get(`${API}/admin/talent/passes`),
        axios.get(`${API}/admin/talent/offers`),
        axios.get(`${API}/admin/talent/interviews`)
      ]);
      
      setCandidates(candidatesRes.data.candidates || []);
      setPasses(passesRes.data.passes || []);
      setOffers(offersRes.data.offers || []);
      setInterviews(interviewsRes.data.interviews || []);
    } catch (error) {
      console.error('Failed to load GoTalent data');
    } finally {
      setLoading(false);
    }
  };

  const toggleCandidateDiscoverable = async (candidateId, currentState) => {
    try {
      await axios.patch(`${API}/admin/talent/candidates/${candidateId}/discoverable`, {
        discoverable: !currentState
      });
      toast.success('Candidate visibility updated');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update candidate');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">GoTalent Management</h1>
          <p className="text-muted-foreground">Manage candidate marketplace and employer access</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground mb-1">Active Candidates</div>
              <div className="text-2xl font-bold text-green-600">{candidates.filter(c => c.discoverable).length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground mb-1">Total Profiles</div>
              <div className="text-2xl font-bold text-foreground">{candidates.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground mb-1">Active Passes</div>
              <div className="text-2xl font-bold text-purple-600">{passes.filter(p => p.status === 'active').length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground mb-1">Total Offers</div>
              <div className="text-2xl font-bold text-foreground">{offers.length}</div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="candidates">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="candidates">Candidates</TabsTrigger>
            <TabsTrigger value="passes">Access Passes</TabsTrigger>
            <TabsTrigger value="offers">Offers</TabsTrigger>
            <TabsTrigger value="interviews">Interviews</TabsTrigger>
          </TabsList>

          {/* Candidates Tab */}
          <TabsContent value="candidates">
            <Card>
              <CardHeader>
                <CardTitle>Talent Candidates ({candidates.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium">Candidate</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Location</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Skills</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr><td colSpan="5" className="text-center py-8">Loading...</td></tr>
                      ) : candidates.length === 0 ? (
                        <tr><td colSpan="5" className="text-center py-8">No candidates yet</td></tr>
                      ) : (
                        candidates.map((candidate) => (
                          <tr key={candidate.id} className="border-b hover:bg-muted/30">
                            <td className="px-4 py-3">
                              <div className="font-medium">{candidate.display_name}</div>
                              <div className="text-xs text-muted-foreground">Score: {candidate.profile_completeness_score}%</div>
                            </td>
                            <td className="px-4 py-3">{candidate.location_suburb}, {candidate.location_state}</td>
                            <td className="px-4 py-3">
                              <div className="text-xs">{candidate.skills?.slice(0, 3).join(', ')}</div>
                            </td>
                            <td className="px-4 py-3">
                              {candidate.discoverable ? (
                                <Badge className="bg-green-100 text-green-800">
                                  <Eye className="w-3 h-3 mr-1" />
                                  Discoverable
                                </Badge>
                              ) : (
                                <Badge className="bg-gray-100 text-gray-800">Hidden</Badge>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <button
                                onClick={() => toggleCandidateDiscoverable(candidate.id, candidate.discoverable)}
                                className="p-2 hover:bg-muted rounded text-sm"
                                title="Toggle visibility"
                              >
                                {candidate.discoverable ? <XCircle className="w-4 h-4 text-red-600" /> : <CheckCircle className="w-4 h-4 text-green-600" />}
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Passes Tab */}
          <TabsContent value="passes">
            <Card>
              <CardHeader>
                <CardTitle>Employer Access Passes ({passes.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {passes.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">No passes purchased yet</div>
                  ) : (
                    passes.map((pass) => (
                      <div key={pass.id} className="p-4 border rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-semibold">{pass.company?.company_name || 'Company'}</div>
                            <div className="text-sm text-muted-foreground">
                              Expires: {new Date(pass.ends_at).toLocaleDateString()}
                            </div>
                          </div>
                          <Badge className={pass.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                            {pass.status}
                          </Badge>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Offers Tab */}
          <TabsContent value="offers">
            <Card>
              <CardHeader>
                <CardTitle>Job Offers ({offers.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium">Company</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Candidate</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Role</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {offers.length === 0 ? (
                        <tr><td colSpan="5" className="text-center py-8">No offers yet</td></tr>
                      ) : (
                        offers.map((offer) => (
                          <tr key={offer.id} className="border-b hover:bg-muted/30">
                            <td className="px-4 py-3">{offer.company?.company_name}</td>
                            <td className="px-4 py-3">{offer.candidate?.display_name}</td>
                            <td className="px-4 py-3">{offer.role_title}</td>
                            <td className="px-4 py-3">
                              <Badge>{offer.status}</Badge>
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground">
                              {new Date(offer.created_at).toLocaleDateString()}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Interviews Tab */}
          <TabsContent value="interviews">
            <Card>
              <CardHeader>
                <CardTitle>Interview Invites ({interviews.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium">Company</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Candidate</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Type</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {interviews.length === 0 ? (
                        <tr><td colSpan="5" className="text-center py-8">No interviews yet</td></tr>
                      ) : (
                        interviews.map((interview) => (
                          <tr key={interview.id} className="border-b hover:bg-muted/30">
                            <td className="px-4 py-3">{interview.company?.company_name}</td>
                            <td className="px-4 py-3">{interview.candidate?.display_name}</td>
                            <td className="px-4 py-3 capitalize">{interview.interview_type?.replace('_', ' ')}</td>
                            <td className="px-4 py-3">
                              <Badge>{interview.status}</Badge>
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground">
                              {new Date(interview.created_at).toLocaleDateString()}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminGoTalent;
